import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Calendar,
  User,
  ArrowRight,
  Tag,
  Clock,
  Share2,
  BookmarkPlus,
  TrendingUp,
  Leaf,
  Droplets,
  Heart,
  X,
} from "lucide-react";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet"; // ✅ Added for SEO
import blogBackgroundImage from "../images/blogBackgroundimage.jpg";

import { motion, AnimatePresence, useInView } from "framer-motion";

// Helper function to calculate reading time
const calculateReadingTime = (content) => {
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return minutes;
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

const modalBackdropVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const modalContentVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: 50,
    transition: { duration: 0.2 },
  },
};

const searchVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 1.1 },
  },
};

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const heroRef = useRef(null);
  const featuredRef = useRef(null);
  const postsRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const isFeaturedInView = useInView(featuredRef, { once: true, amount: 0.2 });
  const isPostsInView = useInView(postsRef, { once: true, amount: 0.1 });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && selectedPost) {
        setSelectedPost(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedPost]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedPost) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedPost]);


  const categories = [
    {
      id: "all",
      name: "All Articles",
      icon: <TrendingUp className="w-4 h-4" />,
    },
    {
      id: "health",
      name: "Health Benefits",
      icon: <Heart className="w-4 h-4" />,
    },
    {
      id: "production",
      name: "Production",
      icon: <Leaf className="w-4 h-4" />,
    },
    {
      id: "cooking",
      name: "Cooking Tips",
      icon: <Droplets className="w-4 h-4" />,
    },
    {
      id: "quality",
      name: "Quality & Standards",
      icon: <TrendingUp className="w-4 h-4" />,
    },
  ];

  const blogPosts = [
    {
      id: 1,
      title:
        "The Complete Guide to Extra Virgin Olive Oil: Health Benefits and Uses",
      excerpt:
        "Discover the remarkable health benefits of extra virgin olive oil, from cardiovascular protection to anti-inflammatory properties. Learn why EVOO is considered liquid gold in the Mediterranean diet.",
      content:
        "Extra virgin olive oil (EVOO) is more than just a cooking ingredient—it's a cornerstone of healthy living. Rich in monounsaturated fats, antioxidants, and polyphenols, EVOO has been scientifically proven to reduce the risk of heart disease, lower inflammation, and support brain health.",
      category: "health",
      author: "Dr. Sarah Martinez",
      date: "2025-10-15",
      image:
        "https://images.unsplash.com/photo-1689776166403-54355e9210da?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&fm=jpg&q=60&w=3000", // Olive oil bottle on a healthy kitchen table
      tags: ["Health", "Nutrition", "Mediterranean Diet", "Antioxidants"],
      featured: true,
    },
    {
      id: 2,
      title: "From Grove to Bottle: Understanding Olive Oil Production Process",
      excerpt:
        "Take a journey through the fascinating process of olive oil production, from harvesting to bottling. Learn what makes quality olive oil stand out.",
      content:
        "The journey of olive oil begins in the grove, where timing is everything. Harvesting olives at peak ripeness ensures optimal flavor and nutritional content. Modern production combines traditional knowledge with cutting-edge technology.",
      category: "production",
      author: "Marco Rossi",
      date: "2025-10-12",
      image:
        "https://images.unsplash.com/photo-1592069200809-90266ffaf0f5?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&fm=jpg&q=60&w=3000", // Olive grove with harvested olives
      tags: ["Production", "Harvesting", "Quality", "Process"],
      featured: true,
    },
    {
      id: 3,
      title:
        "Cooking with Olive Oil: Techniques, Tips, and Temperature Guidelines",
      excerpt:
        "Master the art of cooking with olive oil. Understand smoke points, best practices, and how to maximize flavor in your dishes.",
      content:
        "Contrary to popular belief, extra virgin olive oil is excellent for cooking. With a smoke point around 375-405°F, it's suitable for most cooking methods including sautéing, roasting, and even light frying.",
      category: "cooking",
      author: "Chef Antonio Greco",
      date: "2025-10-10",
      image:
        "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=800&q=80", // Cooking pan with olive oil drizzle
      tags: ["Cooking", "Recipes", "Techniques", "Tips"],
      featured: false,
    },
    {
      id: 4,
      title:
        "How to Identify Authentic Extra Virgin Olive Oil: Quality Indicators",
      excerpt:
        "Learn to distinguish genuine extra virgin olive oil from inferior products. Discover the key quality indicators and certification standards.",
      content:
        "The olive oil market is flooded with products claiming to be extra virgin, but not all meet the strict standards. Authentic EVOO must have zero defects, distinctive fruity aroma, and meet specific chemical parameters.",
      category: "quality",
      author: "Elena Bianchi",
      date: "2025-10-08",
      image:
        "https://images.unsplash.com/photo-1652282565092-874e3a9c67b1?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&fm=jpg&q=60&w=3000", // Bottles of olive oil with labels
      tags: ["Quality", "Standards", "Certification", "Authenticity"],
      featured: false,
    },
    {
      id: 5,
      title: "The Science Behind Olive Oil and Heart Health",
      excerpt:
        "Explore the scientific research linking olive oil consumption to cardiovascular health and longevity.",
      content:
        "Decades of research have established olive oil as a heart-healthy fat. The high concentration of oleic acid and polyphenols helps reduce LDL cholesterol while maintaining HDL levels, protecting against atherosclerosis.",
      category: "health",
      author: "Dr. Michael Thompson",
      date: "2025-10-05",
      image:
        "https://images.unsplash.com/photo-1582536446725-5ad9e6ef0535?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&fm=jpg&q=60&w=3000", // Heart-healthy diet with olive oil
      tags: ["Health", "Research", "Cardiology", "Science"],
      featured: false,
    },
    {
      id: 6,
      title: "Sustainable Olive Farming: Our Commitment to the Environment",
      excerpt:
        "Discover how modern olive oil production embraces sustainability, from water conservation to biodiversity preservation.",
      content:
        "At Solivera, sustainability isn't just a buzzword—it's our commitment to future generations. Our groves employ water-efficient irrigation, natural pest management, and soil conservation practices.",
      category: "production",
      author: "Green Team Solivera",
      date: "2025-10-01",
      image:
        "https://plus.unsplash.com/premium_photo-1730757476209-654a5a49c896?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&fm=jpg&q=60&w=3000", // Sustainable olive farming in the field
      tags: ["Sustainability", "Environment", "Organic", "Farming"],
      featured: false,
    },
  ];

  // Add reading time to posts
  const postsWithReadingTime = blogPosts.map((post) => ({
    ...post,
    readingTime: calculateReadingTime(post.content + " " + post.excerpt),
  }));

  const filteredPosts = postsWithReadingTime.filter((post) => {
    const matchesCategory =
      selectedCategory === "all" || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = postsWithReadingTime.filter((post) => post.featured);

  // Generate Article Schema for SEO
  const generateArticleSchema = (post) => {
    const baseUrl = "https://www.solivera.com";
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Person",
        name: post.author,
      },
      publisher: {
        "@type": "Organization",
        name: "Solivera",
        logo: {
          "@type": "ImageObject",
          url: `${baseUrl}/favicon.png`,
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${baseUrl}/blog/${post.id}`,
      },
      articleSection: post.category,
      keywords: post.tags.join(", "),
      timeRequired: `PT${post.readingTime}M`,
    };
  };

  // Generate Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.solivera.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://www.solivera.com/blog",
      },
    ],
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fff6e3" }}>
      <Helmet>
        {/* Primary SEO Meta Tags */}
        <title>
          Solivera Blog | Olive Oil Health, Production & Cooking Insights
        </title>
        <meta
          name="description"
          content="Discover Solivera's Olive Oil Blog — explore health benefits, production processes, sustainability, and gourmet cooking tips with premium extra virgin olive oil. Expert articles on Mediterranean diet, EVOO quality standards, and sustainable farming practices."
        />
        <meta
          name="keywords"
          content="Solivera olive oil blog, extra virgin olive oil, olive oil health benefits, olive oil production, sustainable olive farming, Mediterranean diet, cooking with olive oil, EVOO, olive oil quality, heart health, antioxidants, polyphenols"
        />
        <meta name="author" content="Solivera" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://www.solivera.com/blog" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Solivera Olive Oil Blog | Expert Insights on Premium EVOO" />
        <meta
          property="og:description"
          content="Expert insights on olive oil health, production, and sustainable Mediterranean practices from Solivera. Learn about premium extra virgin olive oil benefits, cooking techniques, and quality standards."
        />
        <meta
          property="og:image"
          content="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=1200"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Solivera Olive Oil Blog - Premium Extra Virgin Olive Oil Insights" />
        <meta property="og:url" content="https://www.solivera.com/blog" />
        <meta property="og:type" content="blog" />
        <meta property="og:site_name" content="Solivera Olive Oil" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solivera Olive Oil Blog | Expert Insights" />
        <meta
          name="twitter:description"
          content="Learn about olive oil's health benefits, sustainable farming, and Mediterranean cooking from Solivera experts."
        />
        <meta
          name="twitter:image"
          content="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=1200"
        />
        <meta name="twitter:image:alt" content="Solivera Olive Oil Blog" />
        <meta name="twitter:site" content="@solivera" />
        <meta name="twitter:creator" content="@solivera" />

        {/* Additional SEO */}
        <meta name="theme-color" content="#225601" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

        {/* Schema.org Structured Data - Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Solivera Olive Oil Blog",
            url: "https://www.solivera.com/blog",
            description: "Explore Solivera's Olive Oil Blog — expert tips on health, production, sustainability, and cooking with premium extra virgin olive oil.",
            publisher: {
              "@type": "Organization",
              name: "Solivera",
              logo: {
                "@type": "ImageObject",
                url: "https://www.solivera.com/favicon.png",
              },
            },
            image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=1200",
            mainEntityOfPage: "https://www.solivera.com/blog",
            blogPost: postsWithReadingTime.map((post) => generateArticleSchema(post)),
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>

        {/* Article Schemas for each post */}
        {postsWithReadingTime.map((post) => (
          <script key={post.id} type="application/ld+json">
            {JSON.stringify(generateArticleSchema(post))}
          </script>
        ))}
      </Helmet>

      <section
        ref={heroRef}
        className="relative overflow-hidden"
        style={{
          backgroundImage: `url(${blogBackgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "650px",
        }}
        aria-label="Blog hero section"
      >
        <motion.div
          className="absolute inset-0 opacity-10"
          initial={{ opacity: 0 }}
          animate={isHeroInView ? { opacity: 0.1 } : { opacity: 0 }}
          transition={{ duration: 1 }}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, #fff6e3 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          ></div>
        </motion.div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-80 relative">
          <div className="text-center py-20">
            {/* Animated Badge */}
            <motion.div
              className="inline-flex items-center px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: "rgba(255, 246, 227, 0.2)" }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            />

            {/* Animated Heading */}
            <motion.h1
              className="text-5xl md:text-4xl font-bold mb-6"
              style={{ color: "white" }}
              initial={{ opacity: 0, y: 50 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{
                duration: 1,
                ease: [0.25, 0.1, 0.25, 1],
                delay: 0.5,
              }}
            >
              <motion.span
                className="block"
                initial={{ opacity: 0, y: 20, rotateX: -90 }}
                animate={isHeroInView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
              >
                Discover the World of
              </motion.span>
              <motion.span
                className="block mt-2"
                initial={{ opacity: 0, y: 20, rotateX: -90 }}
                animate={isHeroInView ? { opacity: 1, y: 0, rotateX: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.9, ease: "easeOut" }}
              >
                Premium Olive Oil
              </motion.span>
            </motion.h1>

            {/* Search Bar with Animation */}
            <motion.div
              className="max-w-2xl mx-auto relative mt-4"
              variants={searchVariants}
              initial="hidden"
              animate={isHeroInView ? "visible" : "hidden"}
            >
              <motion.div
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                animate={{
                  scale: isSearchFocused ? 1.1 : 1,
                  rotate: isSearchFocused ? [0, -10, 10, -10, 0] : 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <Search
                  className="w-5 h-5"
                  style={{ color: "#225601" }}
                  aria-hidden="true"
                />
              </motion.div>
              <motion.input
                type="text"
                placeholder="Search articles, topics, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className="w-full pl-12 pr-4 py-4 rounded-full text-lg focus:outline-none focus:ring-4 transition-all"
                style={{
                  backgroundColor: "#ffffff",
                  color: "#272e02",
                  boxShadow: isSearchFocused
                    ? "0 15px 50px rgba(34, 86, 1, 0.2)"
                    : "0 10px 40px rgba(0,0,0,0.1)",
                }}
                whileFocus={{ scale: 1.02 }}
                aria-label="Search blog articles"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <nav
        className="sticky top-0 z-40 border-b"
        style={{ backgroundColor: "#ffffff", borderColor: "#225601" }}
        aria-label="Blog category filter"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex overflow-x-auto py-4 space-x-2 scrollbar-hide"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {categories.map((category, index) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="flex items-center px-6 py-3 rounded-full font-medium whitespace-nowrap transition-all duration-300"
                style={{
                  backgroundColor:
                    selectedCategory === category.id ? "#225601" : "#fff6e3",
                  color:
                    selectedCategory === category.id ? "#fff6e3" : "#225601",
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`Filter by ${category.name}`}
                aria-pressed={selectedCategory === category.id}
              >
                <motion.span
                  animate={{
                    rotate: selectedCategory === category.id ? [0, -10, 10, -10, 0] : 0,
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {category.icon}
                </motion.span>
                <span className="ml-2">{category.name}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </nav>

      {/* Featured Posts */}
      {selectedCategory === "all" && !searchTerm && (
        <section ref={featuredRef} className="py-16" aria-labelledby="featured-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="flex items-center justify-between mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isFeaturedInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <h2 id="featured-heading" className="text-4xl font-bold" style={{ color: "#225601" }}>
                Featured Articles
              </h2>
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                <TrendingUp className="w-8 h-8" style={{ color: "#225601" }} aria-hidden="true" />
              </motion.div>
            </motion.div>
            <motion.div
              className="grid md:grid-cols-2 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate={isFeaturedInView ? "visible" : "hidden"}
            >
              {featuredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  className="group relative overflow-hidden rounded-3xl cursor-pointer"
                  style={{
                    backgroundColor: "#ffffff",
                    boxShadow: "0 20px 60px rgba(34, 86, 1, 0.15)",
                  }}
                  variants={itemVariants}
                  onMouseEnter={() => setHoveredCard(post.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => setSelectedPost(post)}
                  whileHover={{ scale: 1.02, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={`Read article: ${post.title}`}
                >
                  <div className="relative h-80 overflow-hidden">
                    <motion.img
                      src={post.image}
                      alt={`${post.title} - Premium extra virgin olive oil article`}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      loading={index < 2 ? "eager" : "lazy"}
                    />
                    <div
                      className="absolute inset-0 opacity-60 group-hover:opacity-40 transition-opacity duration-500"
                      style={{
                        background:
                          "linear-gradient(to top, #225601, transparent)",
                      }}
                    ></div>
                    <div className="absolute top-4 right-4">
                      <motion.span
                        className="px-4 py-2 rounded-full text-sm font-bold"
                        style={{ backgroundColor: "#fff6e3", color: "#225601" }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.2 + 0.5, type: "spring" }}
                      >
                        Featured
                      </motion.span>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center space-x-4 mb-4 flex-wrap">
                      <span
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: "#fff6e3", color: "#225601" }}
                      >
                        {post.category}
                      </span>
                      <div className="flex items-center text-sm" style={{ color: "#272e02" }}>
                        <Clock className="w-4 h-4 mr-1" style={{ color: "#225601" }} aria-hidden="true" />
                        <span>{post.readingTime} min read</span>
                      </div>
                    </div>
                    <h3
                      className="text-2xl font-bold mb-4 group-hover:underline"
                      style={{ color: "#225601" }}
                    >
                      {post.title}
                    </h3>
                    <p
                      className="mb-6 leading-relaxed"
                      style={{ color: "#272e02" }}
                    >
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <User
                          className="w-5 h-5 mr-2"
                          style={{ color: "#225601" }}
                          aria-hidden="true"
                        />
                        <span
                          className="text-sm font-medium"
                          style={{ color: "#272e02" }}
                        >
                          {post.author}
                        </span>
                      </div>
                      <motion.div
                        animate={{
                          x: hoveredCard === post.id ? 5 : 0,
                        }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <ArrowRight
                          className="w-6 h-6"
                          style={{ color: "#225601" }}
                          aria-hidden="true"
                        />
                      </motion.div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section ref={postsRef} className="py-16" aria-labelledby="posts-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            id="posts-heading"
            className="text-4xl font-bold mb-12"
            style={{ color: "#225601" }}
            initial={{ opacity: 0, y: 20 }}
            animate={isPostsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            {selectedCategory === "all"
              ? "All Articles"
              : categories.find((c) => c.id === selectedCategory)?.name}
          </motion.h2>

          <AnimatePresence mode="wait">
            {filteredPosts.length === 0 ? (
              <motion.div
                key="empty"
                className="text-center py-20"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0], y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Droplets
                    className="w-16 h-16 mx-auto mb-4 opacity-50"
                    style={{ color: "#225601" }}
                    aria-hidden="true"
                  />
                </motion.div>
                <p className="text-xl" style={{ color: "#272e02" }}>
                  No articles found. Try adjusting your search or filter.
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="posts"
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                variants={containerVariants}
                initial="hidden"
                animate={isPostsInView ? "visible" : "hidden"}
              >
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className="group overflow-hidden rounded-2xl cursor-pointer"
                    style={{
                      backgroundColor: "#ffffff",
                      boxShadow: "0 10px 40px rgba(34, 86, 1, 0.1)",
                    }}
                    variants={itemVariants}
                    onMouseEnter={() => setHoveredCard(post.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedPost(post)}
                    whileHover={{ scale: 1.03, y: -8 }}
                    whileTap={{ scale: 0.98 }}
                    aria-label={`Read article: ${post.title}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <motion.img
                        src={post.image}
                        alt={`${post.title} - ${post.category} article about olive oil`}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        loading={index < 6 ? "lazy" : "lazy"}
                      />
                      <div
                        className="absolute inset-0 opacity-40 group-hover:opacity-20 transition-opacity duration-500"
                        style={{
                          background:
                            "linear-gradient(to top, #225601, transparent)",
                        }}
                      ></div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                          style={{ backgroundColor: "#fff6e3", color: "#225601" }}
                        >
                          {post.category}
                        </span>
                        <div className="flex items-center text-xs" style={{ color: "#272e02" }}>
                          <Clock className="w-3 h-3 mr-1" style={{ color: "#225601" }} aria-hidden="true" />
                          <span>{post.readingTime} min</span>
                        </div>
                      </div>

                      <h3
                        className="text-xl font-bold mb-3 line-clamp-2 group-hover:underline"
                        style={{ color: "#225601" }}
                      >
                        {post.title}
                      </h3>

                      <p
                        className="mb-4 line-clamp-3 text-sm leading-relaxed"
                        style={{ color: "#272e02" }}
                      >
                        {post.excerpt}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, idx) => (
                          <motion.span
                            key={idx}
                            className="flex items-center px-2 py-1 rounded-full text-xs"
                            style={{
                              backgroundColor: "#fff6e3",
                              color: "#272e02",
                            }}
                            whileHover={{ scale: 1.05 }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 + idx * 0.05 }}
                          >
                            <Tag className="w-3 h-3 mr-1" aria-hidden="true" />
                            {tag}
                          </motion.span>
                        ))}
                      </div>

                      <div
                        className="flex items-center justify-between pt-4 border-t"
                        style={{ borderColor: "#fff6e3" }}
                      >
                        <div className="flex items-center space-x-2">
                          <Calendar
                            className="w-4 h-4"
                            style={{ color: "#225601" }}
                            aria-hidden="true"
                          />
                          <time
                            className="text-sm"
                            style={{ color: "#272e02" }}
                            dateTime={post.date}
                          >
                            {new Date(post.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </time>
                        </div>
                        <div className="flex items-center space-x-2">
                          <motion.button
                            className="p-2 rounded-full transition-all"
                            style={{
                              backgroundColor:
                                hoveredCard === post.id
                                  ? "#fff6e3"
                                  : "transparent",
                            }}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={`Bookmark ${post.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle bookmark
                            }}
                          >
                            <BookmarkPlus
                              className="w-5 h-5"
                              style={{ color: "#225601" }}
                              aria-hidden="true"
                            />
                          </motion.button>
                          <motion.button
                            className="p-2 rounded-full transition-all"
                            style={{
                              backgroundColor:
                                hoveredCard === post.id
                                  ? "#fff6e3"
                                  : "transparent",
                            }}
                            whileHover={{ scale: 1.1, rotate: -5 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={`Share ${post.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle share
                            }}
                          >
                            <Share2
                              className="w-5 h-5"
                              style={{ color: "#225601" }}
                              aria-hidden="true"
                            />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      {/* Modal with AnimatePresence */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={() => setSelectedPost(null)}
            aria-modal="true"
            aria-labelledby="modal-title"
            role="dialog"
          >
            <motion.div
              className="bg-black bg-opacity-70 absolute inset-0"
              onClick={() => setSelectedPost(null)}
            />
            <motion.div
              className="bg-white max-w-3xl w-full max-h-[90vh] rounded-3xl overflow-hidden relative z-10 flex flex-col"
              variants={modalContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                onClick={() => setSelectedPost(null)}
                className="absolute top-4 right-4 z-20 p-2 rounded-full hover:bg-gray-100 transition-colors"
                style={{ color: "#225601" }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Close article modal"
              >
                <X className="w-6 h-6" />
              </motion.button>

              <div className="relative h-96 overflow-hidden">
                <motion.img
                  src={selectedPost.image}
                  alt={`${selectedPost.title} - Featured image`}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
                <div
                  className="absolute inset-0 opacity-40"
                  style={{
                    background: "linear-gradient(to top, #225601, transparent)",
                  }}
                ></div>
              </div>

              <div className="p-8 overflow-y-auto flex-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: "#fff6e3", color: "#225601" }}
                    >
                      {selectedPost.category}
                    </span>
                    <div className="flex items-center space-x-4 text-sm" style={{ color: "#272e02" }}>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" style={{ color: "#225601" }} aria-hidden="true" />
                        <span>{selectedPost.readingTime} min read</span>
                      </div>
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" style={{ color: "#225601" }} aria-hidden="true" />
                        <span>{selectedPost.author}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" style={{ color: "#225601" }} aria-hidden="true" />
                        <time dateTime={selectedPost.date}>
                          {new Date(selectedPost.date).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </time>
                      </div>
                    </div>
                  </div>

                  <h2
                    id="modal-title"
                    className="text-3xl font-bold mb-4"
                    style={{ color: "#225601" }}
                  >
                    {selectedPost.title}
                  </h2>
                  <p className="text-gray-700 mb-4 leading-relaxed text-lg">
                    {selectedPost.content}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {selectedPost.tags.map((tag, i) => (
                      <motion.span
                        key={i}
                        className="flex items-center px-3 py-1 rounded-full text-sm"
                        style={{
                          backgroundColor: "#fff6e3",
                          color: "#272e02",
                        }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + i * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <Tag className="w-3 h-3 mr-1" aria-hidden="true" />
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Blog;
